import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Lightbulb,
  Palette,
  MessageSquare,
  Shield,
  Users,
  Target,
  FolderOpen,
  Globe,
  Link,
  BarChart3,
  Download,
} from "lucide-react";
import ScoreGaugePanel from "@/components/ruby-ai-v2/dashboard/ScoreGaugePanel";
import type { SubNavItem } from "@/components/ruby-ai-v2/dashboard/ScoreGaugePanel";
import DataPanel from "@/components/ruby-ai-v2/dashboard/DataPanel";
import ActionPlanIssuesPanel from "@/components/ruby-ai-v2/dashboard/ActionPlanIssuesPanel";
import AskRubyBrandWidget from "@/components/ruby-ai-v2/dashboard/AskRubyBrandWidget";
import EmptyState from "@/components/ruby-ai-v2/dashboard/EmptyState";
import WidgetExportBtn from "@/components/ruby-ai-v2/reports/WidgetExportBtn";
import { loadLatestAudits, loadLatestAuditByShortcut } from "@/components/ruby-ai-v2/services/auditStorageService";
import SiteSwitcher from "@/components/ruby-ai-v2/dashboard/SiteSwitcher";
import ReportSwitcher from "@/components/ruby-ai-v2/dashboard/ReportSwitcher";
import { useSiteContext } from "@/components/ruby-ai-v2/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import RubyExportDialog from "@/components/ruby-ai-v2/reports/RubyExportDialog";
import { buildFullExportData } from "@/components/ruby-ai-v2/reports/exportDataUtils";

const tabs = [
  { id: "overview", label: "Overview", icon: Lightbulb },
  { id: "identity", label: "Identity", icon: Palette },
  { id: "voice", label: "Voice", icon: MessageSquare },
  { id: "authority", label: "Authority", icon: Shield },
  { id: "audience", label: "Audience", icon: Users },
  { id: "competitors", label: "Competitors", icon: Target },
] as const;

type TabId = (typeof tabs)[number]["id"];

function formatTimeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

const heroDefaults: Record<TabId, {
  badge: string; title: string; centerLabel: string; subtitle: string; metricLabels: string[];
}> = {
  overview: { badge: "Brand Insight", title: "Brand Health Score", centerLabel: "BRAND", subtitle: "Run a Brand Audit to assess your overall brand health.", metricLabels: ["Business Rating", "Reviews", "Image Count", "Recommendations"] },
  identity: { badge: "Visual Identity", title: "Visual Identity Score", centerLabel: "IDENTITY", subtitle: "Run a Visual Identity Review to assess brand visuals.", metricLabels: ["Colors Found", "Fonts", "Logo Detected", "Image Count"] },
  voice: { badge: "Brand Voice", title: "Voice & Tone Score", centerLabel: "VOICE", subtitle: "Run a Voice & Personality audit to assess brand tone.", metricLabels: ["Tone", "Style", "Keywords", "Pages Analysed"] },
  authority: { badge: "Authority", title: "Authority Score", centerLabel: "AUTHORITY", subtitle: "Run an Authority Analysis to see your domain authority.", metricLabels: ["Domain Authority", "Backlinks", "Referring Domains", "Anchor Texts"] },
  audience: { badge: "Audience", title: "Audience Score", centerLabel: "AUDIENCE", subtitle: "Run an Audience Insights audit to understand your audience.", metricLabels: ["Organic Traffic", "Keywords", "Top Pages", "Market"] },
  competitors: { badge: "Competitors", title: "Competitive Score", centerLabel: "COMPETE", subtitle: "Run a Competitor Benchmarking audit to compare brands.", metricLabels: ["Competitors", "Common Keywords", "Traffic Gap", "Authority Gap"] },
};

const TAB_SUBNAV: Record<TabId, Omit<SubNavItem, "hasData">[]> = {
  overview: [
    { id: "brand-audit", label: "Brand Audit", shortcutId: "brand-audit" },
    { id: "authority-ov", label: "Authority", shortcutId: "authority-analysis" },
    { id: "ai-presence", label: "AI Presence", shortcutId: "brand-ai-presence" },
  ],
  identity: [
    { id: "visual-id", label: "Visual Identity", shortcutId: "visual-identity" },
  ],
  voice: [
    { id: "voice-pers", label: "Voice & Personality", shortcutId: "voice-personality" },
  ],
  authority: [
    { id: "auth-anal", label: "Authority Analysis", shortcutId: "authority-analysis" },
    { id: "backlink-prof", label: "Backlink Profile", shortcutId: "brand-backlink-profile" },
    { id: "ai-pres-auth", label: "AI Presence", shortcutId: "brand-ai-presence" },
  ],
  audience: [
    { id: "audience-ins", label: "Audience Insights", shortcutId: "audience-insights" },
  ],
  competitors: [
    { id: "comp-bench", label: "Competitor Benchmarking", shortcutId: "competitor-benchmarking" },
  ],
};


const MetricCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
    <div className="text-xs mb-1" style={{ color: "#9AA0B3" }}>{label}</div>
    <div className="text-xl font-bold" style={{ color: highlight ? "#FF9A4A" : "#E8E4FF" }}>{value}</div>
  </div>
);

const BrandInsightDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { activeSite, selectedReportId } = useSiteContext();
  const { user } = useAuth();
  const userId = user?.id;
  const auditContextKey = JSON.stringify([userId, activeSite?.domain, selectedReportId]);
  const auditContextRef = useRef(auditContextKey);
  auditContextRef.current = auditContextKey;
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [liveAuditData, setLiveAuditData] = useState<any | null>(null);
  const [activeSubNav, setActiveSubNav] = useState<string>(TAB_SUBNAV.overview[0].id);
  const [availableAudits, setAvailableAudits] = useState<Set<string>>(new Set());
  const [lastAuditMeta, setLastAuditMeta] = useState<{ region: string; domain: string; createdAt: string } | null>(null);
  const [exportDialog, setExportDialog] = useState<{ open: boolean; title: string; tier: "widget" | "chat" | "full"; data: any[]; auditResult?: any }>({ open: false, title: "", tier: "widget", data: [] });

  const openFullReportExport = () => {
    if (!liveAuditData) return;
    setExportDialog({ open: true, title: "Brand Insight Full Report", tier: "full", data: buildFullExportData(liveAuditData,  liveAuditData.requestedAuditType || "brand-audit"), auditResult: liveAuditData });
  };

  useEffect(() => {
    const firstItem = TAB_SUBNAV[activeTab]?.[0];
    if (firstItem) setActiveSubNav(firstItem.id);
  }, [activeTab]);

  const loadDashboardAudit = async (domain?: string) => {
    if (!userId || !domain) return;
    setLiveAuditData(null);
    setLastAuditMeta(null);
    const audit = await loadLatestAuditByShortcut({ userId, cardId: "brand-insight", shortcutId: "authority44-import", domain, reportId: selectedReportId });
    if (auditContextRef.current !== auditContextKey) return;
    if (audit) {
      setLiveAuditData(audit.structured_data);
      setLastAuditMeta({ region: audit.region, domain: audit.domain, createdAt: audit.created_at });
      return;
    } else {
      setLiveAuditData(null);
      setLastAuditMeta(null);
    }
  };

  useEffect(() => {
    if (!userId) return;
    loadLatestAudits({ userId, cardId: "brand-insight", limit: 50 }).then((audits) => {
      const found = new Set<string>();
      for (const a of audits) found.add(a.shortcut_id);
      setAvailableAudits(found);
    }).catch((err) => console.warn("[BrandInsight] Failed to load:", err));
    loadDashboardAudit(activeSite?.domain);
  }, [userId]);

  useEffect(() => {
    if (activeSite?.domain && userId) loadDashboardAudit(activeSite.domain);
    else {
      setLiveAuditData(null);
      setLastAuditMeta(null);
    }
  }, [activeSite?.domain, userId, selectedReportId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const mainEl = document.querySelector("main");
    if (mainEl) mainEl.scrollTop = 0;
  }, []);

  const handleSubNavClick = async (item: SubNavItem) => {
    if (!userId) return;
    setActiveSubNav(item.id);
    const saved = await loadLatestAuditByShortcut({ userId, cardId: "brand-insight", shortcutId: item.shortcutId, domain: activeSite?.domain });
    if (auditContextRef.current !== auditContextKey) return;
    if (saved) {
      setLiveAuditData(saved.structured_data as any);
      setLastAuditMeta({ region: saved.region, domain: saved.domain, createdAt: saved.created_at });
    } else {
      setLiveAuditData(null);
    }
  };

  const handleAuditComplete = (result: any) => {
    setLiveAuditData(result);
    setLastAuditMeta({ region: "", domain: result.domain || "", createdAt: new Date().toISOString() });
    if (result.requestedAuditType) {
      setAvailableAudits((prev) => new Set([...prev, result.requestedAuditType]));
    }
  };

  const d = liveAuditData;
  const bi = d?.businessInfo;
  const bv = d?.brandVoice;
  const vi = d?.visualIdentity;
  const bl = d?.backlinks;
  const dm = d?.domainMetrics;
  const comps = d?.competitors;
  const aiVis = d?.aiVisibility;

  const defaults = heroDefaults[activeTab];
  const hasAnyData = bi || bv || vi || bl || dm || comps || aiVis;
  const score = bi?.rating ? Math.round(bi.rating * 20) : dm?.authorityScore ?? 0;
  const subtitle = hasAnyData ? `Live audit for ${d?.domain ?? "your domain"}` : defaults.subtitle;

  const buildMetrics = () => {
    if (bi) return [
      { label: "Rating", value: `${bi.rating ?? 0}/5` },
      { label: "Reviews", value: String(bi.reviewCount ?? 0) },
      { label: "Images", value: String(bv?.imageCount ?? 0) },
      { label: "Recommendations", value: String(d?.recommendations?.length ?? 0) },
    ];
    if (dm) return [
      { label: "Authority", value: `${dm.authorityScore ?? 0}/100` },
      { label: "Traffic", value: (dm.organicTraffic ?? 0).toLocaleString() },
      { label: "Keywords", value: (dm.organicKeywords ?? 0).toLocaleString() },
      { label: "Backlinks", value: (dm.backlinks ?? 0).toLocaleString() },
    ];
    if (bl) return [
      { label: "Domain Authority", value: `${bl.domainAuthority?.domainAuthority ?? 0}/100` },
      { label: "Total Backlinks", value: (bl.total ?? 0).toLocaleString() },
      { label: "Referring Domains", value: (bl.referringDomains ?? 0).toLocaleString() },
      { label: "Top Sources", value: String(bl.topSources?.length ?? 0) },
    ];
    if (aiVis) {
      const engines = Object.values(aiVis.engines ?? {}).filter((e: any) => !e.error);
      const avgBrand = engines.length ? Math.round(engines.reduce((s: number, e: any) => s + (e.brandPresence ?? 0), 0) / engines.length) : 0;
      return [
        { label: "Brand Presence", value: `${avgBrand}%` },
        { label: "Engines", value: String(engines.length) },
        { label: "Leaderboard", value: String(aiVis.leaderboard?.length ?? 0) },
        { label: "Position", value: engines.length ? (engines.reduce((s: number, e: any) => s + (e.averagePosition ?? 0), 0) / engines.length).toFixed(1) : "0" },
      ];
    }
    return defaults.metricLabels.map((l) => ({ label: l, value: "0" }));
  };

  const renderTabContent = () => {
    // Brand Audit / Visual Identity / Voice data (from ruby-ai-brand-analysis)
    if (bi || bv || vi) {
      return (
        <>
          {bi && (
            <DataPanel title="Business Information (Google Places)" icon={<Globe className="w-4 h-4" />} actions={<WidgetExportBtn title="Business Information" data={bi ? [{ Name: bi.name, Rating: bi.rating, Reviews: bi.reviewCount, Phone: bi.phone, Website: bi.website, Address: bi.address }] : []} />}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <MetricCard label="Business Name" value={bi.name ?? "—"} />
                <MetricCard label="Rating" value={`${bi.rating ?? 0} / 5`} highlight />
                <MetricCard label="Reviews" value={String(bi.reviewCount ?? 0)} />
                <MetricCard label="Phone" value={bi.phone ?? "—"} />
                <MetricCard label="Website" value={bi.website ? "✓ Found" : "—"} />
                <MetricCard label="Open Now" value={bi.isOpen ? "Yes" : "—"} />
              </div>
              {bi.address && <div className="mt-3 text-sm" style={{ color: "#9AA0B3" }}>{bi.address}</div>}
            </DataPanel>
          )}

          {vi && (
            <DataPanel title="Visual Identity" icon={<Palette className="w-4 h-4" />} actions={<WidgetExportBtn title="Visual Identity" data={vi ? [{ Colors: vi.colorCount ?? vi.colors?.length, Fonts: vi.fonts?.length, Logo: vi.logoDetected ? "Yes" : "No", Images: bv?.imageCount }] : []} />}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <MetricCard label="Colors Found" value={String(vi.colorCount ?? vi.colors?.length ?? 0)} highlight />
                <MetricCard label="Fonts" value={Array.isArray(vi.fonts) ? String(vi.fonts.length) : "—"} />
                <MetricCard label="Logo Detected" value={vi.logoDetected ? "Yes" : "No"} />
                <MetricCard label="Images" value={String(bv?.imageCount ?? 0)} />
              </div>
              {vi.colors?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {vi.colors.slice(0, 10).map((color: string, i: number) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                      <div className="w-4 h-4 rounded" style={{ background: color }} />
                      <span className="text-xs font-mono" style={{ color: "#C4BDE0" }}>{color}</span>
                    </div>
                  ))}
                </div>
              )}
            </DataPanel>
          )}

          {bv && (
            <DataPanel title="Brand Voice & Tone" icon={<MessageSquare className="w-4 h-4" />} actions={<WidgetExportBtn title="Brand Voice" data={bv ? [{ Tone: bv.tone, Style: bv.style, Keywords: bv.keywords?.join(", ") }] : []} />}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <MetricCard label="Tone" value={bv.tone ?? "—"} />
                <MetricCard label="Style" value={bv.style ?? "—"} />
              </div>
              {bv.keywords?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {bv.keywords.slice(0, 12).map((kw: string, i: number) => (
                    <span key={i} className="text-xs px-2 py-1 rounded-full" style={{ background: "rgba(91,61,245,0.15)", color: "#8B7BF7" }}>{kw}</span>
                  ))}
                </div>
              )}
            </DataPanel>
          )}
        </>
      );
    }

    // Backlink / Authority data (from ruby-ai-backlink-analysis)
    if (bl) {
      const topSources: any[] = bl.topSources ?? [];
      const anchors: any[] = bl.anchors ?? [];
      return (
        <>
          <DataPanel title="Authority & Trust Metrics" icon={<Shield className="w-4 h-4" />} actions={<WidgetExportBtn title="Authority Metrics" data={[{ DA: bl?.domainAuthority?.domainAuthority, PA: bl?.domainAuthority?.pageAuthority, Backlinks: bl?.total, "Ref. Domains": bl?.referringDomains, New: bl?.newLinks?.length, Lost: bl?.lostLinks?.length }]} />}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <MetricCard label="Domain Authority" value={`${bl.domainAuthority?.domainAuthority ?? 0}/100`} highlight />
              <MetricCard label="Page Authority" value={`${bl.domainAuthority?.pageAuthority ?? 0}/100`} />
              <MetricCard label="Total Backlinks" value={(bl.total ?? 0).toLocaleString()} />
              <MetricCard label="Referring Domains" value={(bl.referringDomains ?? 0).toLocaleString()} />
              <MetricCard label="New Links" value={String(bl.newLinks?.length ?? 0)} />
              <MetricCard label="Lost Links" value={String(bl.lostLinks?.length ?? 0)} />
            </div>
          </DataPanel>

          {topSources.length > 0 && (
            <DataPanel title="Top Referring Domains" icon={<Link className="w-4 h-4" />} actions={<WidgetExportBtn title="Top Referring Domains" data={topSources.map(s => ({ Domain: s.domain, Backlinks: s.backlinks, DA: s.domainAuthority, PA: s.pageAuthority }))} />}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                      {["Domain", "Backlinks", "DA", "PA"].map((h) => (
                        <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {topSources.slice(0, 10).map((s: any, i: number) => (
                      <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                        <td className="py-2.5 px-2 font-mono text-xs" style={{ color: "#8B7BF7" }}>{s.domain}</td>
                        <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{s.backlinks}</td>
                        <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{s.domainAuthority ?? "—"}</td>
                        <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{s.pageAuthority ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DataPanel>
          )}

          {anchors.length > 0 && (
            <DataPanel title="Anchor Text Distribution" icon={<BarChart3 className="w-4 h-4" />} actions={<WidgetExportBtn title="Anchor Text" data={anchors.map(a => ({ Anchor: a.anchor || "(empty)", Backlinks: a.backlinksCount, "Ref. Domains": a.referringDomainsCount }))} />}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                      {["Anchor", "Backlinks", "Referring Domains"].map((h) => (
                        <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {anchors.slice(0, 10).map((a: any, i: number) => (
                      <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                        <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{a.anchor || "(empty)"}</td>
                        <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{a.backlinksCount ?? 0}</td>
                        <td className="py-2.5 px-2" style={{ color: "#8B7BF7" }}>{a.referringDomainsCount ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DataPanel>
          )}
        </>
      );
    }

    // SEO / Audience / Competitor data (from ruby-ai-seo-analysis)
    if (dm || comps) {
      return (
        <>
          {dm && (
            <DataPanel title="Domain Overview" icon={<Globe className="w-4 h-4" />} actions={<WidgetExportBtn title="Domain Overview" data={dm ? [{ Authority: dm.authorityScore, Traffic: dm.organicTraffic, Keywords: dm.organicKeywords, Backlinks: dm.backlinks, "Ref. Domains": dm.referringDomains }] : []} />}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <MetricCard label="Authority Score" value={`${dm.authorityScore ?? 0}/100`} highlight />
                <MetricCard label="Organic Traffic" value={`${(dm.organicTraffic ?? 0).toLocaleString()}/mo`} />
                <MetricCard label="Keywords" value={(dm.organicKeywords ?? 0).toLocaleString()} />
                <MetricCard label="Backlinks" value={(dm.backlinks ?? 0).toLocaleString()} />
                <MetricCard label="Referring Domains" value={(dm.referringDomains ?? 0).toLocaleString()} />
              </div>
            </DataPanel>
          )}

          {comps?.length > 0 && (
            <DataPanel title="Competitor Comparison" icon={<Target className="w-4 h-4" />} actions={<WidgetExportBtn title="Competitors" data={comps?.map((c: any) => ({ Domain: c.domain, Authority: c.authorityScore, Traffic: c.traffic, "Common KW": c.commonKeywords })) || []} />}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                      {["Domain", "Authority", "Traffic", "Common Keywords"].map((h) => (
                        <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {comps.slice(0, 10).map((c: any, i: number) => (
                      <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                        <td className="py-2.5 px-2 font-mono text-xs" style={{ color: "#8B7BF7" }}>{c.domain}</td>
                        <td className="py-2.5 px-2 font-bold" style={{ color: "#E8E4FF" }}>{c.authorityScore ?? "—"}</td>
                        <td className="py-2.5 px-2" style={{ color: "#FF9A4A" }}>{(c.traffic ?? 0).toLocaleString()}</td>
                        <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{c.commonKeywords ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DataPanel>
          )}
        </>
      );
    }

    // AI Visibility data (from ruby-ai-ai-visibility)
    if (aiVis) {
      const engines = aiVis.engines ?? {};
      const lb: any[] = aiVis.leaderboard ?? [];
      return (
        <>
          <DataPanel title="AI Engine Visibility" icon={<Lightbulb className="w-4 h-4" />} actions={<WidgetExportBtn title="AI Engine Visibility" data={Object.entries(engines).map(([name, eng]: [string, any]) => ({ Engine: name, "Brand %": eng.brandPresence ?? 0, "Link %": eng.linkPresence ?? 0, Position: eng.averagePosition ?? 0 }))} />}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Engine", "Brand Presence", "Link Presence", "Position"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(engines).map(([name, eng]: [string, any], i) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2 font-medium" style={{ color: "#8B7BF7" }}>{name}</td>
                      {eng.error ? (
                        <td colSpan={3} className="py-2.5 px-2 text-xs" style={{ color: "#F87171" }}>{eng.error}</td>
                      ) : (
                        <>
                          <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{eng.brandPresence ?? 0}%</td>
                          <td className="py-2.5 px-2" style={{ color: "#4ADE80" }}>{eng.linkPresence ?? 0}%</td>
                          <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(eng.averagePosition ?? 0).toFixed(1)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataPanel>

          {lb.length > 0 && (
            <DataPanel title="AI Leaderboard" icon={<Target className="w-4 h-4" />} actions={<WidgetExportBtn title="AI Leaderboard" data={lb.map((e: any, i: number) => ({ Rank: i + 1, Domain: e.domain, "Brand %": e.brandPresence, "Link %": e.linkPresence, Position: e.averagePosition }))} />}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                      {["#", "Domain", "Brand %", "Link %", "Position"].map((h) => (
                        <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lb.slice(0, 10).map((entry: any, i: number) => (
                      <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                        <td className="py-2.5 px-2 font-bold" style={{ color: i < 3 ? "#FF9A4A" : "#9AA0B3" }}>{i + 1}</td>
                        <td className="py-2.5 px-2 font-mono text-xs" style={{ color: "#C4BDE0" }}>{entry.domain}</td>
                        <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{entry.brandPresence ?? 0}%</td>
                        <td className="py-2.5 px-2" style={{ color: "#4ADE80" }}>{entry.linkPresence ?? 0}%</td>
                        <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(entry.averagePosition ?? 0).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DataPanel>
          )}
        </>
      );
    }

    // No data — empty state
    return (
      <>
        <DataPanel title="Brand Data" icon={<Lightbulb className="w-4 h-4" />} actions={<WidgetExportBtn title="Brand Data" data={[]} />}>
          <EmptyState title="No brand data yet" description="Run a Brand Audit, Authority Analysis, or AI Presence audit to see your brand metrics." />
        </DataPanel>
        <DataPanel title="Detailed Insights" icon={<BarChart3 className="w-4 h-4" />} actions={<WidgetExportBtn title="Brand Data" data={[]} />}>
          <EmptyState title="No insights yet" description="Choose an audit from Ask Ruby to populate this dashboard with real data." />
        </DataPanel>
      </>
    );
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden" style={{ background: "#08080E" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0, background: "radial-gradient(620px 420px at 88% -6%, rgba(139,92,246,.15), transparent 70%), radial-gradient(560px 460px at 102% 108%, rgba(255,122,24,.10), transparent 70%)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.4, zIndex: 0, backgroundImage: "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)", backgroundSize: "46px 46px", WebkitMaskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)", maskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)" }} />

      <div className="relative z-[1] px-6 md:px-10 lg:px-14 py-6 mx-auto" style={{ ["--accent" as string]: "#FF7A18", maxWidth: "1480px" } as React.CSSProperties}>
        {/* Header */}
        <div className="flex items-center gap-3.5 mb-1.5">
          <button onClick={() => navigate("/dashboard")} aria-label="Back to dashboard" className="grid place-items-center w-[38px] h-[38px] flex-none rounded-[10px] border border-white/[0.07] bg-og01-card text-og01-text-secondary transition-colors hover:border-white/[0.12] hover:text-og01-text">
            <ArrowLeft className="w-[18px] h-[18px]" />
          </button>
          <SiteSwitcher onSiteChange={(domain) => loadDashboardAudit(domain)} />
          <ReportSwitcher />
          <h1 className="flex items-center gap-2.5 font-heading text-[30px] leading-none text-og01-text">
            <Lightbulb className="w-[26px] h-[26px]" style={{ color: "var(--accent)" }} />
            Brand Insight
          </h1>
          <div className="flex-1" />
          <button onClick={openFullReportExport} className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] border border-white/[0.07] bg-og01-card text-[13.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12]" style={{ color: liveAuditData ? undefined : "#6B6585" }}>
            <Download className="w-[15px] h-[15px]" />Export Full Report
          </button>
          <button onClick={() => navigate("/resource-vault?card=brand-insight")} className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] border-none text-[13.5px] font-semibold text-white transition-all hover:brightness-110" style={{ background: "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)", boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
            <FolderOpen className="w-[15px] h-[15px]" />Saved Reports
          </button>
        </div>

        {/* Metadata */}
        {lastAuditMeta && (
          <div className="flex items-center gap-5 mb-4 pl-[52px] text-[12.5px] text-og01-text-muted">
            {[
              lastAuditMeta.domain ? <>Domain: <b className="font-medium text-og01-text-secondary">{lastAuditMeta.domain}</b></> : null,
              lastAuditMeta.createdAt ? <>Last Run: <b className="font-medium text-og01-text-secondary">{formatTimeAgo(lastAuditMeta.createdAt)}</b></> : null,
            ].filter(Boolean).map((item, i) => (
              <span key={i}>{item}</span>
            ))}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-5 p-1.5 rounded-[14px] overflow-x-auto border border-white/[0.07] bg-og01-card">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex items-center gap-2 px-[15px] py-2.5 rounded-[9px] text-[13.5px] font-medium whitespace-nowrap transition-all hover:text-og01-text"
                style={isActive ? { color: "var(--accent)", background: "color-mix(in srgb, var(--accent) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 28%, transparent)" } : { color: "#9AA0B3", border: "1px solid transparent" }}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        {/* Score Gauge with Sub-Nav */}
        <div className="mb-6">
          <ScoreGaugePanel
            score={score} scoreChange={0} period="Last 30 Days" subtitle={subtitle} metrics={buildMetrics()}
            badge={defaults.badge} title={defaults.title} centerLabel={defaults.centerLabel}
            subNavItems={TAB_SUBNAV[activeTab].map((item) => ({ ...item, hasData: availableAudits.has(item.shortcutId) }))}
            activeSubNav={activeSubNav} onSubNavClick={handleSubNavClick}
          />
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6" style={{ minHeight: "calc(100vh - 200px)" }}>
            <ActionPlanIssuesPanel cardId="brand-insight" title="Recommended Actions" />
            {renderTabContent()}
          </div>

          <div className="lg:col-span-2 flex flex-col">
            <div className="sticky top-6 flex-1" style={{ height: "calc(100vh - 48px)" }}>
              <AskRubyBrandWidget activeTab={activeTab} onIssueFixed={() => loadDashboardAudit(activeSite?.domain)} />
            </div>
          </div>
        </div>
      </div>

      <RubyExportDialog
        open={exportDialog.open}
        onClose={() => setExportDialog((p) => ({ ...p, open: false }))}
        title={exportDialog.title}
        tier={exportDialog.tier}
        data={exportDialog.data}
        fileName={`OG01_Brand_Insight_${lastAuditMeta?.domain || "report"}`}
        auditResult={exportDialog.auditResult}
        domain={lastAuditMeta?.domain}
      />
    </div>
  );
};

export default BrandInsightDashboard;

